import React, { useState, useEffect } from "react";
import Box from "@mui/material/Box";
import Modal from "@mui/material/Modal";
import IconButton from "@mui/material/IconButton";
import { FaTimes } from "react-icons/fa";
import axios from "axios";
import BtnLoader from "Components/Common/Loader/BtnLoader";

const style = {
  position: "absolute",
  top: "50%",
  left: "50%",
  transform: "translate(-50%, -50%)",
  width: "90%",
  maxWidth: 420,
  bgcolor: "background.paper",
  border: "none",
  borderRadius: 4,
  boxShadow: "0 25px 50px rgba(0,0,0,0.15)",
  p: 4,
  maxHeight: "90vh",
  overflowY: "auto",
};

const AddEmojiGiftForm = ({
  addEmojiModal,
  handleAddModalClose,
  fetchAllEmojis,
  staffId,
  categories,
  fetchCategories,
  preselectedCategoryId,
}) => {
  const [isLoading, setIsLoading] = useState(false);
  const [formData, setFormData] = useState({
    emoji: "",
    emojiName: "",
    emojiPrice: "",
    category: "",
    isActive: true,
  });

  // Sync preselected category whenever modal opens
  useEffect(() => {
    if (addEmojiModal) {
      setFormData((prev) => ({
        ...prev,
        category: preselectedCategoryId || "",
      }));
    }
  }, [addEmojiModal, preselectedCategoryId]);

  const handleChange = (e) => {
    const { name, value, type, checked } = e.target;
    setFormData({ ...formData, [name]: type === "checkbox" ? checked : value });
  };

  const createStaffActivityLog = async (actionType, description) => {
    try {
      await axios.post(
        `${process.env.REACT_APP_API_URL}/staffs-activity-log/create-staffs-activity`,
        { staff: staffId, action: actionType, description }
      );
    } catch (error) {
      console.error("Activity log failed:", error.message);
    }
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    const data = JSON.stringify({
      name: formData.emojiName,
      emoji: formData.emoji,
      description: "Its emoji gift package.",
      coinDeduct: formData.emojiPrice,
      category: formData.category,
      isActive: formData.isActive,
    });

    setIsLoading(true);
    axios
      .request({
        method: "post",
        maxBodyLength: Infinity,
        url: `${process.env.REACT_APP_API_URL}/emoji-package/create-emoji-packages`,
        headers: { "Content-Type": "application/json" },
        data,
      })
      .then(async () => {
        await createStaffActivityLog(
          "CREATE_EMOJI",
          `Created new emoji gift - "${formData.emojiName}" - ${formData.emoji} with price ₹${formData.emojiPrice}`
        );
        fetchAllEmojis();
        handleClose();
      })
      .catch((error) => console.log(error))
      .finally(() => setIsLoading(false));
  };

  const handleClose = () => {
    setFormData({
      emoji: "",
      emojiName: "",
      emojiPrice: "",
      category: "",
      isActive: true,
    });
    handleAddModalClose();
  };

  const preselectedName = categories.find(
    (c) => c._id === preselectedCategoryId
  )?.name;

  return (
    <Modal open={addEmojiModal} onClose={handleClose}>
      <Box sx={style}>
        <IconButton
          onClick={handleClose}
          sx={{ position: "absolute", right: 8, top: 8, color: "#c51369" }}
        >
          <FaTimes size={14} />
        </IconButton>

        <h2 className="mb-1 text-center text-2xl font-bold text-gray-800">
          Add Emoji Gift
        </h2>

        {preselectedName ? (
          <p className="mb-5 text-center text-sm text-gray-400">
            Adding to{" "}
            <span className="font-semibold text-[#c51369]">
              {preselectedName}
            </span>
          </p>
        ) : (
          <p className="mb-5 text-center text-sm text-gray-400">
            Fill in the details to add a new emoji gift.
          </p>
        )}

        <form onSubmit={handleSubmit} className="space-y-4">
          {/* Emoji */}
          <div>
            <label className="mb-1.5 block text-sm font-semibold uppercase tracking-wide text-gray-500">
              Emoji (URL or emoji character)
            </label>
            <div className="flex items-center gap-2">
              {formData.emoji && (
                <span className="flex h-10 w-10 shrink-0 items-center justify-center rounded-lg border border-gray-200 bg-gray-50 text-2xl">
                  {formData.emoji.startsWith("http") ? (
                    <img
                      src={formData.emoji}
                      alt="preview"
                      className="h-8 w-8 object-contain"
                    />
                  ) : (
                    formData.emoji
                  )}
                </span>
              )}
              <input
                type="text"
                name="emoji"
                placeholder="e.g. 🥰 or https://..."
                value={formData.emoji}
                onChange={handleChange}
                className="w-full rounded-xl border border-gray-200 bg-gray-50 px-3 py-2.5 text-sm text-gray-800 transition focus:border-[#c51369] focus:bg-white focus:outline-none focus:ring-2 focus:ring-[#c51369]/30"
                required
              />
            </div>
          </div>

          {/* Emoji Name */}
          <div>
            <label className="mb-1.5 block text-sm font-semibold uppercase tracking-wide text-gray-500">
              Emoji Name
            </label>
            <input
              type="text"
              name="emojiName"
              placeholder="e.g. Heart Eyes"
              value={formData.emojiName}
              onChange={handleChange}
              className="w-full rounded-xl border border-gray-200 bg-gray-50 px-3 py-2.5 text-sm text-gray-800 transition focus:border-[#c51369] focus:bg-white focus:outline-none focus:ring-2 focus:ring-[#c51369]/30"
              required
            />
          </div>

          {/* Emoji Price */}
          <div>
            <label className="mb-1.5 block text-sm font-semibold uppercase tracking-wide text-gray-500">
              Price (₹ coins)
            </label>
            <div className="relative">
              <span className="absolute left-3 top-1/2 -translate-y-1/2 text-sm font-semibold text-gray-400">
                ₹
              </span>
              <input
                type="number"
                name="emojiPrice"
                placeholder="50"
                value={formData.emojiPrice}
                onChange={handleChange}
                className="w-full rounded-xl border border-gray-200 bg-gray-50 py-2.5 pl-7 pr-3 text-sm text-gray-800 transition focus:border-[#c51369] focus:bg-white focus:outline-none focus:ring-2 focus:ring-[#c51369]/30"
                required
              />
            </div>
          </div>

          {/* Category */}
          <div>
            <label className="mb-1.5 block text-sm font-semibold uppercase tracking-wide text-gray-500">
              Category
            </label>
            {preselectedCategoryId ? (
              // Locked display when opened from a category row
              <div className="flex items-center gap-2 rounded-xl border border-gray-200 bg-gray-100 px-3 py-2.5 text-sm text-gray-700">
                <span className="h-2 w-2 rounded-full bg-[#c51369]" />
                {preselectedName}
                <span className="ml-auto text-xs text-gray-400">
                  (pre-selected)
                </span>
              </div>
            ) : (
              <select
                name="category"
                value={formData.category}
                onChange={handleChange}
                className="w-full rounded-xl border border-gray-200 bg-gray-50 px-3 py-2.5 text-sm text-gray-800 transition focus:border-[#c51369] focus:bg-white focus:outline-none focus:ring-2 focus:ring-[#c51369]/30"
                required
              >
                <option value="">Select a category</option>
                {categories.map((cat) => (
                  <option key={cat._id} value={cat._id}>
                    {cat.name}
                  </option>
                ))}
              </select>
            )}
          </div>

          {/* Active Status */}
          <div className="flex items-center justify-between rounded-xl border border-gray-200 bg-gray-50 px-3 py-2.5">
            <div>
              <p className="text-sm font-semibold text-gray-700">
                Active Status
              </p>
            </div>
            <label className="relative inline-flex cursor-pointer items-center">
              <input
                type="checkbox"
                name="isActive"
                checked={formData.isActive}
                onChange={handleChange}
                className="peer sr-only"
              />
              <div className="peer h-6 w-11 rounded-full bg-gray-300 transition-colors duration-200 after:absolute after:left-[2px] after:top-[2px] after:h-5 after:w-5 after:rounded-full after:bg-white after:shadow after:transition-all after:content-[''] peer-checked:bg-[#c51369] peer-checked:after:translate-x-full" />
            </label>
          </div>

          {/* Submit */}
          {isLoading ? (
            <button
              type="button"
              className="flex w-full items-center justify-center rounded-xl bg-[#c51369] py-2.5"
            >
              <BtnLoader />
            </button>
          ) : (
            <button
              type="submit"
              className="w-full rounded-xl bg-[#c51369] py-2.5 text-sm font-semibold text-white transition-colors hover:bg-[#a10f5b]"
            >
              Add Gift
            </button>
          )}
        </form>
      </Box>
    </Modal>
  );
};

export default AddEmojiGiftForm;
